import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
	Box,
	Chip,
	Grid,
	IconButton,
	Link as MaterialLink,
	Theme,
	Tooltip,
	Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData, HIDE_PERIL_LIMITS_ENABLE } from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import {
	getExposureConfig,
	getPerilConfigFromExposureConfig,
	getPolicyOverrideResourceTimestamp,
	isPerilActive,
	isPolicyEditable,
} from '../../../util/util';
import { FastForm } from '../../fastForm';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';
import AddPerilModal from './AddPerilModal';

interface Props {
	authData: AuthData;
	userHasWriteAccess?: boolean;
	policyData: PolicyData;
}

const useStyles = makeStyles((theme: Theme) => ({
	titleWrapper: {
		display: 'flex',
		alignItems: 'flex-end',
	},
	title: {
		textTransform: 'capitalize',
		flexGrow: 1,
		fontSize: '18px',
	},
	auxDataLink: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
	removeIcon: {
		position: 'relative',
		top: theme.spacing(1),
	},
}));

const PerilDetails = (props: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const params = useParams();
	const { pathname } = useLocation();

	const { authData, userHasWriteAccess, policyData } = props;

	const {
		policy,
		productConfig,
		selectedQuote,
		selectedExposure,
		policyViewTimestamp,
		fields: form,
		saveFastForm,
		setLoading,
	} = policyData;
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const { perilId } = params;

	const selectedPerilIndex = selectedExposure.perils.findIndex(
		(peril) => peril.displayId === perilId,
	);
	const selectedPeril = selectedExposure.perils[selectedPerilIndex];

	if (!selectedPeril) {
		return null;
	}
	const isPolicyIssued = !!policy.issuedTimestamp;
	const isActive = isPolicyIssued
		? isPerilActive(selectedPeril, selectedExposure, policyViewTimestamp)
		: true;
	const overrideResourcesAsOfTimestamp = getPolicyOverrideResourceTimestamp(
		policy,
		selectedQuote,
	);
	const exposureConfig = getExposureConfig(productConfig, selectedExposure);

	const perilConfig = getPerilConfigFromExposureConfig(
		exposureConfig,
		selectedPeril,
	);
	const perilConfigs = exposureConfig.perils;
	const displayName = perilConfig.displayName;

	const isInEditMode =
		authData.hasPermission('quote.update') &&
		isPolicyEditable(policy, selectedQuote);
	const hidePerilLimitsEnabled =
		authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={selectedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const renderRemovePeril = () => {
		return (
			<Tooltip title={'Remove ' + t('peril.singular.upper')}>
				<IconButton
					aria-label='Remove'
					sx={{ position: 'relative', top: '8px' }}
					onClick={() => setIsConfirmModalOpen(true)}
					className={classes.removeIcon}
				>
					<DeleteIcon
						sx={{
							'&:hover': {
								backgroundColor: '#f4433',
							},
						}}
					/>
				</IconButton>
			</Tooltip>
		);
	};

	const perilLocator = selectedPeril.locator;
	const exposureLocator = selectedExposure.locator;

	const removePeril = async () => {
		setIsConfirmModalOpen(false);
		setLoading(true);
		try {
			await saveFastForm(getValues(), dirtyFields, reset, {
				removePeril: { perilLocator, exposureLocator },
			});
			successToast('Successfully removed peril');

			navigate(
				`/policy/${params.policyId}/quotes/${params.quoteId}/exposures/${params.exposureId}/perils`,
			);
		} catch (error) {
			errorToast('Error removing peril');
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
		} finally {
			setLoading(false);
		}
	};

	const renderActions = () => {
		return (
			<AddPerilModal
				perilConfigs={perilConfigs}
				exposureLocator={selectedExposure.locator}
				policyData={policyData}
				authData={authData}
			/>
		);
	};

	return (
		<React.Fragment key={pathname}>
			<Box className={classes.titleWrapper}>
				<Typography className={classes.title}>{displayName}</Typography>
				{authData.hasPermission('auxData.fetch') && (
					<MaterialLink
						className={classes.auxDataLink}
						component={Link}
						to={`auxData`}
					>
						Aux Data
					</MaterialLink>
				)}
				{!isActive && (
					<Chip
						color='default'
						label='Inactive'
						icon={<ErrorOutlineRoundedIcon />}
					/>
				)}
				{isInEditMode && renderRemovePeril()}
				{isConfirmModalOpen && renderRemovePerilModal()}
			</Box>
			<hr />
			{!hidePerilLimitsEnabled ? <h4>Limits</h4> : null}
			<Grid container spacing={2}>
				<FastForm
					formName={exposureLocator + '.' + perilLocator}
					form={form}
					configVersion={props.policyData.productConfig.configVersion}
					authData={props.authData}
					productName={productConfig.name}
					disabled={!isInEditMode || userHasWriteAccess}
					save={saveFastForm}
					setLoading={setLoading}
					effectiveDate={overrideResourcesAsOfTimestamp}
				/>
			</Grid>
			{isInEditMode && renderActions()}
		</React.Fragment>
	);
};

export default withPolicyContext<Props>(PerilDetails);
